import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { PDFDocumentProxy } from 'ng2-pdf-viewer';
import { MultimodalService } from 'shared/services/multimodal.service';
import { analyticsTrackQuoteProfit } from 'app/utils/analytics-utils';

@Component({
  selector: 'app-pdf-preview',
  templateUrl: './pdf-preview.component.html',
  styleUrls: ['./pdf-preview.component.scss']
})
export class PdfPreviewComponent implements OnChanges {
  @Input() offerTitle!: string;

  @Input() offerDocumentId!: string;

  @Input() offerRevision!: number;

  @Input() isDealer!: boolean;

  @Input() pdfSrc = '';

  @Input() offerPrice?: string | number;

  @Output() pdfReady = new EventEmitter<boolean>();

  page = 1;

  zoom = 0.5;

  minZoom = 0.25;

  maxZoom = 1.5;

  totalPages!: number;

  isLoaded = false;

  pdfData!: PDFDocumentProxy;

  currentMultimodalStepIndex = 0;

  constructor(private multimodalService: MultimodalService) {
    this.multimodalService.currentStepIndex$.subscribe((index) => {
      this.currentMultimodalStepIndex = index;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pdfSrc) {
      this.page = 1;

      if (!changes.pdfSrc.currentValue) {
        this.isLoaded = false;
      }

      this.pdfReady.emit(this.isLoaded);
    }
  }

  afterLoadComplete(pdfData: PDFDocumentProxy): void {
    this.totalPages = pdfData.numPages;
    this.pdfData = pdfData;
    this.isLoaded = true;
    this.pdfReady.emit(this.isLoaded);
  }

  nextPage(): void {
    this.page = this.page < this.totalPages ? this.page + 1 : this.totalPages;
  }

  prevPage(): void {
    this.page = this.page > 1 ? this.page - 1 : 1;
  }

  increaseZoom(): void {
    this.zoom = this.zoom < this.maxZoom ? this.zoom + 0.125 : this.maxZoom;
  }

  decreaseZoom(): void {
    this.zoom = this.zoom > this.minZoom ? this.zoom - 0.125 : this.minZoom;
  }

  print(): void {
    let printFrame = document.getElementById('print-frame') as HTMLIFrameElement;

    if (printFrame) {
      printFrame.remove();
    }

    this.pdfData.getData().then((res: any) => {
      const src = this.getFileUrl(res.buffer);
      printFrame = document.createElement('iframe');
      printFrame.id = 'print-frame';
      printFrame.style.display = 'none';
      printFrame.src = src;
      document.body.appendChild(printFrame);
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        printFrame.contentWindow!.print();
        if (this.isDealer && this.offerPrice) {
          analyticsTrackQuoteProfit(
            'quote|print|' + this.getOfferPathString(),
            this.offerPrice
          );
        }
      }, 0);
    });
  }

  download(): void {
    let downloadLink = document.getElementById('download-link') as HTMLAnchorElement;

    if (downloadLink) {
      downloadLink.remove();
    }

    this.pdfData.getData().then((res: any) => {
      const href = this.getFileUrl(res.buffer);
      downloadLink = document.createElement('a');
      downloadLink.id = 'download-link';
      downloadLink.style.display = 'none';
      downloadLink.href = href;
      downloadLink.download = this.getFileName();
      document.body.appendChild(downloadLink);
      setTimeout(() => {
        downloadLink.click();
        if (this.isDealer && this.offerPrice) {
          analyticsTrackQuoteProfit(
            'quote|download|' + this.getOfferPathString(),
            this.offerPrice
          );
        }
      }, 0);
    });
  }

  getFileUrl(buffer: BlobPart): string {
    return URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
  }

  getFileName(): string {
    const revision = this.getOfferRevision();
    const offerTitle = this.offerTitle ? this.offerTitle : 'offer-V' + revision;

    return `${offerTitle}`;
  }

  getOfferRevision(): number {
    return this.offerRevision >= 0 ? this.offerRevision + 1 : 1;
  }

  getOfferPathString(): string {
    return this.offerDocumentId + '/' + this.getOfferRevision();
  }

  goBack() {
    this.multimodalService.goBack();
  }
}
